import { useCallback, useContext, useMemo } from 'react'

import getBufferETAFlag from '../../../../../../helpers/getBufferETAFlag'
import { useShoppingCartContext } from '../../../../providers/ShoppingCartProvider'
import { useLocationContext } from '../../../../../../contexts/location.context'
import { SearchContext } from '../../../../../common/context/SearchContextProvider'
import { LocalContext } from '../../../../../../helpers/useLocalContext'
import config from '../../../../../../../config/local-config'
import shoppingCartV2API from '../../../../api'
import { GTM } from '../../../../../../helpers'
import useExecuteGTM from './useExecuteGTM'
import nrLogger from '../../../../../../helpers/nrLogger'
import StopWatch from '../../../../logging/StopWatch'
import trackApplyCouponValidationInPL from '../../../../logging/gtm/trackApplyCouponValidationInPL'
import { useEconomyCookie, useEconomyEnabled } from '../../../../../../hooks/useEconomyFlag'
import { useVehicleContext } from '../../../../../../contexts/vehicle.context'
import updateCartCounter from '../../../../logging/header/updateCartCounter'
import getCookieRegex from '../../../../../../helpers/getCookieRegex'
import { usePasAddToCartCookie, usePasAddToCartEnabled } from '../../../../../../hooks/usePasAddToCartEnabled'
import { useUnfoldedShippingEnabled, useUnfoldedShippingCookie } from '../../../../../../hooks/useUnfoldedShipping'

const useAddToCartPL = params => {
  const {
    index,
    data,
    isNotAvailable,
    setNotify,
    setCartLoading,
    invObj,
    groundDeliveryTime,
    pageType,
    widgetName = '',
    executeEcomATCEvent,
  } = params

  const {
    cartIdExists,
    getCartId,
    setCart,
    setCartId,
    getIdentifier,
  } = useShoppingCartContext()

  const locationState = useLocationContext()
  const storedZipCode = (locationState || {}).postalCode
  const storedState = (locationState || {}).state
  const storedCity = (locationState || {}).city
  const buffereta = Number(getBufferETAFlag())

  const vehicleState = useVehicleContext()
  const context = useContext(SearchContext)
  const { searchState = {} } = context || {}
  const { products = {} } = searchState
  const selectedVehicle = useMemo(() => {
    if (pageType === 'cart') {
      const vehicleDB = vehicleState?.vehicleDB || []
      return vehicleDB.find(element => element.selected) || null
    }
    return products?.selectedVehicle || {}
  }, [vehicleState, pageType, products])

  const { localContext } = useContext(LocalContext)
  const { history } = localContext
  const cartId = getCartId()

  const executeGTM = useExecuteGTM({
    index,
    pageType,
    data,
    selectedVehicle,
    invObj,
    groundDeliveryTime,
    widgetName,
    executeEcomATCEvent,
    cartId,
  })

  const isEconomyShippingEnabled = useEconomyEnabled()
  const economyShippingVariant = useEconomyCookie()
  const isPasAddToCartEnabled = usePasAddToCartEnabled()
  const pasAddToCartVariant = usePasAddToCartCookie()
  const isUnfoldedShippingEnabled = useUnfoldedShippingEnabled()
  const unfoldedShippingVariant = useUnfoldedShippingCookie()

  return useCallback(
    async () => {
      if (isNotAvailable) {
        setNotify(true)
        return
      }

      try {
        setCartLoading(true)
        const item = {
          product: {
            productId: parseInt(data.id, 10),
            sku: data.sku,
            brand: {
              name: data.brand,
            },
          },
          modifiers: {
            quantity: 1,
          },
          referrer: __BROWSER__ ? window.location.href : '',
          referrerUrl: __BROWSER__ ? window.location.href : '',
          buffereta,
        }

        if (selectedVehicle && selectedVehicle.year && selectedVehicle.make && selectedVehicle.model) {
          item.modifiers = {
            ...item.modifiers,
            vehicle: {
              details: {
                ...(selectedVehicle?.vehicleType && { vehicleType: selectedVehicle.vehicleType }),
                year: parseInt(selectedVehicle.year, 10),
                make: selectedVehicle.make,
                model: selectedVehicle.model,
                submodel: selectedVehicle.submodel || '',
                engine: selectedVehicle.engine || '',
              },
            },
          }
        }

        const items = [item]

        let res = {}
        const identifier = getIdentifier()

        const cartFeatureFlags = {
          featureFlags: [
            {
              name: 'isEconomyShippingEnabled',
              value: isEconomyShippingEnabled,
              variantValue: economyShippingVariant,
            },
            {
              name: 'isPasAddToCartEnabled',
              value: isPasAddToCartEnabled,
              variantValue: pasAddToCartVariant,
            },
            {
              name: 'isUnfoldedShippingEnabled',
              value: isUnfoldedShippingEnabled,
              variantValue: unfoldedShippingVariant,
            },
          ],
        }

        /* eslint-disable camelcase */
        const cookie_opt_dec = getCookieRegex('opt_dec')
        const cookie_cp2 = getCookieRegex('cp2')
        const metaData = {
          ...cookie_opt_dec ? { cookie_opt_dec } : {},
          ...cookie_cp2 ? { cookie_cp2 } : {},
        }

        const [baseZipCode] = (storedZipCode || '').split('-')
        const payload = {
          items,
          modifiers: {
            location: {
              zipcode: baseZipCode,
              state: storedState,
              city: storedCity,
            },
            ...cartFeatureFlags,
          },
          domain: config.hydra.site,
          buffereta,
          ...metaData ? { metaData } : {},
        }

        const timer = new StopWatch()
        timer.start()

        if (cartIdExists() === false) {
          res = await shoppingCartV2API.items.create(identifier, payload)
          setCartId(res.cartId || '')
        } else {
          try {
            let cartModifiers = {}
            if (payload?.modifiers?.featureFlags) {
              cartModifiers = {
                modifiers: {
                  featureFlags: payload?.modifiers?.featureFlags,
                },
              }
            }
            res = await shoppingCartV2API.items.add(
              identifier,
              cartId,
              { items, buffereta, ...cartModifiers, ...metaData ? { metaData } : {} },
            )

            if (res && cartId !== res.cartId) {
              setCartId(res.cartId)
            }
          } catch (error1) {
            nrLogger.error('ShoppingCartV2.useAddToCartPL.error1', error1)
            if (error1.response && error1.response.status === 404) {
              res = await shoppingCartV2API.items.create(identifier, payload)
              setCartId(res.cartId || '')
            } else {
              throw error1
            }
          }
        }
        timer.stop()

        trackApplyCouponValidationInPL(res, timer.getElapsed())

        setCart(res)
        updateCartCounter(res)

        executeGTM()
        history.push('/cart')
      } catch (error2) {
        nrLogger.error('ShoppingCartV2.useAddToCartPL.error2', error2)
        setCartLoading(false)
        GTM.addToCartFail('SERP add to cart API call failed.')
        throw error2
      }
    },
    [
      cartIdExists,
      cartId,
      setCart,
      setCartId,
      getIdentifier,
      data,
      isNotAvailable,
      setNotify,
      storedZipCode,
      storedState,
      storedCity,
      buffereta,
      setCartLoading,
      history,
      selectedVehicle,
      executeGTM,
      isEconomyShippingEnabled,
      economyShippingVariant,
      isPasAddToCartEnabled,
      pasAddToCartVariant,
      isUnfoldedShippingEnabled,
      unfoldedShippingVariant,
    ],
  )
}

export default useAddToCartPL
